"use client";
import Script from "next/script";
import { useCallback, useEffect, useRef } from "react";
import { useMixpanel } from "../hooks/useMixpanel";

// Define allowed cookie categories
type CookieCategory =
  | "necessary"
  | "functional"
  | "analytics"
  | "performance"
  | "advertisement"
  | "other";

// Define the detail type for the CustomEvent from CookieYes
interface CookieYesConsentDetail {
  accepted: CookieCategory[];
  rejected: CookieCategory[];
}

// Define a nullable boolean type
type ConsentStatus = Record<CookieCategory, boolean | null>;

type CookieYesWindow = Window & {
  getCkyConsent?: () => { isUserActionCompleted?: boolean };
};

/**
 * Only load this script if we're in integration (CookieYes' staging mode environment)
 * or in production AND if the feature flag is enabled. This allows us to control
 * the loading of the script during our tests.
 */

export const CookieYesScript = () => {
  const cookieYesScriptTag = process.env.COOKIE_YES_SCRIPT_TAG;
  const analytics = useMixpanel();
  const hasBannerLoaded = useRef(false);

  const handleBannerLoad = useCallback(() => {
    hasBannerLoaded.current = true;
    // Next keeps the loaded script across provider remounts, but CookieYes does
    // not emit banner_load again. Store readiness on that same page's script.
    document.getElementById("cookieyes")?.setAttribute("data-cookieyes-banner-ready", "true");
  }, []);

  const handleConsentUpdate = useCallback(
    async (event: Event) => {
      // Initialization can restore a previous choice. Track only updates after
      // the banner has initialized and the vendor reports a completed choice.
      if (!hasBannerLoaded.current) {
        return;
      }
      const consent = (window as CookieYesWindow).getCkyConsent?.();
      if (consent?.isUserActionCompleted !== true) {
        return;
      }
      const customEvent = event as CustomEvent<CookieYesConsentDetail>;
      const { accepted, rejected } = customEvent.detail;

      const categories: CookieCategory[] = [
        "necessary",
        "functional",
        "analytics",
        "performance",
        "advertisement",
        "other",
      ];

      // Construct consentStatus with explicit handling of null cases
      const consentStatus: ConsentStatus = categories.reduce((acc, category) => {
        if (accepted.includes(category)) {
          acc[category] = true;
        } else if (rejected.includes(category)) {
          acc[category] = false;
        } else {
          acc[category] = null;
        }
        return acc;
      }, {} as ConsentStatus);

      await analytics.track("Consent Banner - Updated Preferences", consentStatus);
    },
    [analytics]
  );

  useEffect(() => {
    if (!cookieYesScriptTag) {
      return;
    }

    document.addEventListener("cookieyes_banner_load", handleBannerLoad);
    document.addEventListener("cookieyes_consent_update", handleConsentUpdate);
    // beforeInteractive can initialize CookieYes before this effect attaches.
    // Its banner container remains present even when a saved choice hides it.
    if (
      document.querySelector(".cky-consent-container") ||
      document.getElementById("cookieyes")?.getAttribute("data-cookieyes-banner-ready") === "true"
    ) {
      handleBannerLoad();
    }

    return () => {
      document.removeEventListener("cookieyes_banner_load", handleBannerLoad);
      document.removeEventListener("cookieyes_consent_update", handleConsentUpdate);
    };
  }, [cookieYesScriptTag, handleBannerLoad, handleConsentUpdate]);

  if (!cookieYesScriptTag) {
    return null;
  }

  return (
    <Script
      id="cookieyes"
      type="text/javascript"
      src={`https://cdn-cookieyes.com/client_data/${cookieYesScriptTag}/script.js`}
      strategy="beforeInteractive"
    />
  );
};
